import React, { useContext } from 'react';
import { css, useTheme } from '@emotion/react';
import { type InlineEditorHints } from '@bbk/ui/inline-editing';
import { type RestOneUpLaCard } from '@bbkAdminUtils/api-client/rest-la-cards-types';
import { type RestModalForwards } from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { cs } from '@bbkAdminUtils/css-utils';
import CustomerHtml from '@bbkFunnel/components/customer_html/customer_html';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';
import { FunnelContext } from '@bbkFunnelComponents/FunnelContext';
import { LossGridItem } from '@bbkFunnelComponents/loss/sections/grid-item';
import { LossTitle } from '@bbkFunnelComponents/loss/sections/title';
import { makeClassNameSafe } from '@bbkFunnelUtils/utils';

type Props = {
  card: RestOneUpLaCard;
  onClick?: () => void;
  handleLossCardAction?: (opts: { action: RestModalForwards }) => void;
};
const RenderLossOneUpImage: React.FC<Props> = ({
  card,
  card: {
    requires: { image, title, forwards, imageStretch },
  },
  onClick,
  handleLossCardAction,
}) => {
  const theme = useTheme() as EmotionTheme;
  const { isPreview } = useContext(FunnelContext);
  const redemptionEnabled = !card.max_redemptions_reached;

  const handleClick = isPreview
    ? onClick
    : forwards
      ? () => {
          if (redemptionEnabled) {
            handleLossCardAction?.({ action: forwards });
          }
        }
      : () => undefined;
  return (
    <LossGridItem
      styles={css`
        display: flex;
        flex-direction: column;
        padding-top: 0;
        padding-bottom: 15px;
        @media (max-width: 480px) {
          padding-top: 0;
          padding-bottom: 15px;
        }

        .bbk-loss-component-title {
          padding-top: 30px;
        }

        .la-one-up-image {
          img {
            width: 100%;
          }
        }

        &.no-title .la-one-up-image {
          padding-top: 15px;
        }

        &.image-stretch.no-title .la-one-up-image {
          padding-top: 0;
        }

        &.image-stretch .la-one-up-image {
          margin-left: -30px;
          margin-right: -30px;
          margin-bottom: -15px;
          @media (max-width: 480px) {
            margin-left: -15px;
            margin-right: -15px;
          }

          img {
            border-bottom-right-radius: ${theme.borderRadius}px;
            border-bottom-left-radius: ${theme.borderRadius}px;
          }
        }

        &.no-title.image-stretch .la-one-up-image {
          img {
            border-radius: ${theme.borderRadius}px;
          }
        }

        &.image-stretch .la-one-up-image,
        &.no-title .la-one-up-image {
          flex: 1;
          display: flex;
          align-items: center;
        }

        &.la-disabled {
          cursor: not-allowed;
        }
      `}
      classes={cs('loss-one-up', makeClassNameSafe(card.name), {
        'image-stretch': imageStretch,
        'no-title': !title,
        'la-disabled': !redemptionEnabled,
      })}
      onClick={handleClick}
      hasFullCardAction={!!forwards}
    >
      <span data-part="title">
        {title && (
          <LossTitle>
            <CustomerHtml value={title} />
          </LossTitle>
        )}
      </span>
      <div className="la-one-up-image" data-part="image">
        <img src={image} alt="HERO" />
      </div>
    </LossGridItem>
  );
};
const LossOneUpImage = Object.assign(RenderLossOneUpImage, {
  editorHints: {
    title: {
      path: ['requires', 'title'],
      control: 'richText',
      selector: '[data-part="title"]',
      placement: 'top-end',
    },
    image: {
      path: ['requires'],
      control: 'group',
      selector: '[data-part="image"]',
      placement: 'top-end',
      fields: [
        {
          path: ['image'],
          control: 'image',
          label: 'Hero',
          options: {
            helpLabel: 'Recommended width: 460px',
          },
        },
        {
          path: ['imageStretch'],
          control: 'boolean',
          label: 'Stretch image to edge',
        },
      ],
    },
  } satisfies InlineEditorHints,
});
export default LossOneUpImage;
